import { useEffect, useState } from 'react';
import { Clock, AlertTriangle } from 'lucide-react';
import { cn } from '../../lib/cn';

interface CountdownProps {
  expiresAt: number;
  onExpire?: () => void;
  className?: string;
}

function format(seconds: number) {
  const m = Math.floor(seconds / 60);
  const s = Math.max(0, seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export function Countdown({ expiresAt, onExpire, className }: CountdownProps) {
  const [remaining, setRemaining] = useState(() =>
    Math.max(0, Math.round((expiresAt - Date.now()) / 1000)),
  );

  useEffect(() => {
    let fired = false;
    const tick = () => {
      const next = Math.max(0, Math.round((expiresAt - Date.now()) / 1000));
      setRemaining(next);
      if (next <= 0 && !fired) {
        fired = true;
        onExpire?.();
      }
    };
    const id = window.setInterval(tick, 1000);
    tick();
    return () => window.clearInterval(id);
  }, [expiresAt, onExpire]);

  const isUrgent = remaining > 0 && remaining <= 60;
  const isExpired = remaining <= 0;

  return (
    <div
      role="timer"
      aria-live={isUrgent ? 'polite' : 'off'}
      className={cn(
        'inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-mono tabular-nums transition-colors',
        isExpired || isUrgent ? 'bg-danger-soft text-danger' : 'bg-accent-soft text-accent-fg',
        className,
      )}
    >
      {isExpired || isUrgent ? <AlertTriangle size={14} /> : <Clock size={14} />}
      {isExpired ? 'expired' : `expires in ${format(remaining)}`}
    </div>
  );
}
